import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import EmbedBuilder, { EmbedData, EMPTY_EMBED } from '../../components/dashboard/EmbedBuilder';
import PageSkeleton from '../../components/dashboard/PageSkeleton';
import SearchableChannelSelect from '../../components/dashboard/SearchableChannelSelect';
import {
  Save, Loader2, ToggleLeft, ToggleRight, Hash, Mail, Megaphone,
  CheckCircle2, AlertCircle, Info, Sparkles,
  ChevronDown, ChevronRight, MessageSquare, Shield,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════ */
/*  Types                                                         */
/* ═══════════════════════════════════════════════════════════════ */

interface WelcomeConfigProps {
  guildId: string;
  guildData: any;
}

interface WelcomeState {
  public_welcome: {
    enabled: boolean;
    channel_id: string | null;
    embed: EmbedData;
  };
  dm_welcome: {
    enabled: boolean;
    embed: EmbedData;
  };
}

const DEFAULT_STATE: WelcomeState = {
  public_welcome: {
    enabled: false,
    channel_id: null,
    embed: {
      ...EMPTY_EMBED,
      title: 'Welcome to {server}!',
      description: 'Hey {user}, welcome to **{server}**! You are member #{memberCount}.',
      color: '#FF6B9D',
    },
  },
  dm_welcome: {
    enabled: false,
    embed: {
      ...EMPTY_EMBED,
      title: 'Welcome to {server}!',
      description:
        "Hi {user}! Welcome to **{server}**.\n\nHere are some things to get you started:\n\n• Check out our rules channel\n• Grab some roles\n• Say hi in general chat!",
      color: '#FF6B9D',
      footer: 'Enjoy your stay!',
    },
  },
};

/* ═══════════════════════════════════════════════════════════════ */
/*  Collapsible Section                                           */
/* ═══════════════════════════════════════════════════════════════ */

function Section({
  title,
  subtitle,
  icon: Icon,
  enabled,
  onToggle,
  badge,
  children,
  defaultOpen = true,
}: {
  title: string;
  subtitle: string;
  icon: any;
  enabled?: boolean;
  onToggle?: () => void;
  badge?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-dark-800/60 border border-white/[0.06] rounded-2xl overflow-hidden transition-all">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-5 py-4 cursor-pointer select-none hover:bg-white/[0.02] transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            {badge && (
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-primary/20 text-primary rounded-full">
                {badge}
              </span>
            )}
          </div>
          <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">{subtitle}</p>
        </div>

        {/* Toggle */}
        {onToggle && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="transition-colors shrink-0"
          >
            {enabled ? (
              <ToggleRight className="w-10 h-10 text-primary" />
            ) : (
              <ToggleLeft className="w-10 h-10 text-gray-600" />
            )}
          </button>
        )}

        {/* Chevron */}
        <div className="shrink-0 text-gray-600">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </div>

      {/* Content */}
      {open && <div className="px-5 pb-5 space-y-5 border-t border-white/[0.04] pt-5">{children}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  Variable Reference Card                                       */
/* ═══════════════════════════════════════════════════════════════ */

function VariableReference() {
  const [open, setOpen] = useState(false);
  const vars = [
    { key: '{user}', desc: 'Mentions the new member' },
    { key: '{user.name}', desc: 'Username (no mention)' },
    { key: '{user.id}', desc: 'User ID' },
    { key: '{user.avatar}', desc: 'User avatar URL' },
    { key: '{user.created_at}', desc: 'User creation date (YYYY-MM-DD)' },
    { key: '{server}', desc: 'Server name' },
    { key: '{server.id}', desc: 'Server ID' },
    { key: '{server.icon}', desc: 'Server icon URL' },
    { key: '{server.banner}', desc: 'Server banner URL' },
    { key: '{memberCount}', desc: 'Total member count' },
    { key: '{inviter}', desc: 'Mentions the inviter' },
    { key: '{inviter.name}', desc: 'Inviter name' },
    { key: '{inviter.id}', desc: 'Inviter ID' },
    { key: '{inviter.invites}', desc: 'Total invites by inviter' },
  ];

  return (
    <div className="bg-[#5865F2]/8 border border-[#5865F2]/15 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#8b9cf7] hover:text-[#a8b6ff] transition-colors"
      >
        <Info className="w-4 h-4 shrink-0" />
        <span className="font-medium">Template Variables</span>
        {open ? <ChevronDown className="w-3.5 h-3.5 ml-auto" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
      </button>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          {vars.map((v) => (
            <div key={v.key} className="flex items-center gap-2">
              <code className="text-[11px] font-mono bg-dark-800/60 px-1.5 py-0.5 rounded text-primary/80">{v.key}</code>
              <span className="text-[11px] text-gray-500">{v.desc}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  Main Component                                                */
/* ═══════════════════════════════════════════════════════════════ */

export default function WelcomeConfig({ guildId, guildData }: WelcomeConfigProps) {
  const { token } = useAuth();
  const [config, setConfig] = useState<WelcomeState>(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fetchError, setFetchError] = useState<string | null>(null);

  const channels = guildData?.channels || [];

  /* ─── Fetch existing config ─── */
  useEffect(() => {
    if (!guildId || !token) return;

    let cancelled = false;
    const controller = new AbortController();

    async function fetchConfig() {
      const t0 = performance.now();
      try {
        const API_BASE = import.meta.env.VITE_API_URL || '';
        const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/welcome`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (cancelled) return;

        const elapsed = Math.round(performance.now() - t0);

        if (res.ok) {
          const data = await res.json();
          console.log(`[Welcome] Config loaded in ${elapsed}ms`);

          const mergeEmbed = (saved: any, defaults: EmbedData): EmbedData => ({
            ...EMPTY_EMBED,
            ...defaults,
            ...(saved || {}),
            fields: saved?.fields || defaults.fields || [],
          });

          setConfig({
            public_welcome: {
              enabled: data.public_welcome?.enabled ?? false,
              channel_id: data.public_welcome?.channel_id ?? null,
              embed: mergeEmbed(data.public_welcome?.embed, DEFAULT_STATE.public_welcome.embed),
            },
            dm_welcome: {
              enabled: data.dm_welcome?.enabled ?? false,
              embed: mergeEmbed(data.dm_welcome?.embed, DEFAULT_STATE.dm_welcome.embed),
            },
          });
          setFetchError(null);
        } else {
          console.warn(`[Welcome] Config fetch failed: HTTP ${res.status} (${elapsed}ms)`);
          setFetchError(`Failed to load config (HTTP ${res.status}). Using defaults.`);
        }
      } catch (err: any) {
        if (cancelled || err?.name === 'AbortError') return;
        console.error('[Welcome] Config fetch error:', err);
        setFetchError('Could not connect to server. Using defaults.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchConfig();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [guildId, token]);

  /* ─── Save config ─── */
  async function handleSave() {
    if (!token) return;
    setSaving(true);
    setSaveStatus('idle');

    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/welcome`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (res.ok) {
        setSaveStatus('success');
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        const errText = await res.text().catch(() => '');
        console.error('[Welcome] Save failed:', res.status, errText);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 4000);
      }
    } catch (err) {
      console.error('[Welcome] Save error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 4000);
    } finally {
      setSaving(false);
    }
  }

  if (loading || !guildData) {
    return <PageSkeleton rows={3} />;
  }

  const publicEnabled = config.public_welcome.enabled;
  const dmEnabled = config.dm_welcome.enabled;

  return (
    <div className="space-y-5 max-w-5xl">
      {/* ─── Page Header ─── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-display font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Welcome System
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Configure how new members are greeted — public channel messages, private DMs, and custom embeds
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.97] ${saveStatus === 'success'
            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
            : saveStatus === 'error'
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-primary hover:bg-primary-dark text-white hover:shadow-lg hover:shadow-primary/25'
            }`}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveStatus === 'success' ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : saveStatus === 'error' ? (
            <AlertCircle className="w-4 h-4" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Failed' : 'Save Changes'}
        </button>
      </div>

      {/* ─── Error Banner ─── */}
      {fetchError && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-amber-300 text-sm">{fetchError}</p>
        </div>
      )}

      {/* ─── Quick Status ─── */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-xl border px-4 py-3 flex items-center gap-3 transition-colors ${publicEnabled
          ? 'bg-green-500/5 border-green-500/20'
          : 'bg-dark-800/40 border-white/[0.06]'
          }`}>
          <Megaphone className={`w-5 h-5 ${publicEnabled ? 'text-green-400' : 'text-gray-600'}`} />
          <div>
            <p className={`text-sm font-medium ${publicEnabled ? 'text-green-300' : 'text-gray-500'}`}>
              Public Welcome
            </p>
            <p className="text-[11px] text-gray-600">
              {publicEnabled ? `#${channels.find((c: any) => c.id === config.public_welcome.channel_id)?.name || 'No channel set'}` : 'Disabled'}
            </p>
          </div>
        </div>
        <div className={`rounded-xl border px-4 py-3 flex items-center gap-3 transition-colors ${dmEnabled
          ? 'bg-green-500/5 border-green-500/20'
          : 'bg-dark-800/40 border-white/[0.06]'
          }`}>
          <Mail className={`w-5 h-5 ${dmEnabled ? 'text-green-400' : 'text-gray-600'}`} />
          <div>
            <p className={`text-sm font-medium ${dmEnabled ? 'text-green-300' : 'text-gray-500'}`}>
              DM Welcome
            </p>
            <p className="text-[11px] text-gray-600">
              {dmEnabled ? 'Sends embed to new member DMs' : 'Disabled'}
            </p>
          </div>
        </div>
      </div>

      {/* ─── Variable Reference ─── */}
      <VariableReference />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  PUBLIC WELCOME SECTION                                    */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <Section
        title="Public Welcome"
        subtitle="Send a welcome embed to a channel when a new member joins"
        icon={Megaphone}
        enabled={publicEnabled}
        onToggle={() =>
          setConfig((c) => ({
            ...c,
            public_welcome: { ...c.public_welcome, enabled: !c.public_welcome.enabled },
          }))
        }
        badge={publicEnabled ? 'Active' : undefined}
        defaultOpen={true}
      >
        {publicEnabled ? (
          <div className="space-y-5">
            {/* Channel Selector */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <Hash className="w-4 h-4" />
                Welcome Channel
              </label>
              <SearchableChannelSelect
                value={config.public_welcome.channel_id}
                onChange={(val) =>
                  setConfig((c) => ({
                    ...c,
                    public_welcome: { ...c.public_welcome, channel_id: val },
                  }))
                }
                channels={channels}
                placeholder="Search or select welcome channel..."
              />
              {!config.public_welcome.channel_id && (
                <p className="text-amber-400/70 text-xs mt-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Select a channel for welcome messages to appear
                </p>
              )}
            </div>

            {/* Embed Builder */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wider flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Channel Embed
              </p>
              <EmbedBuilder
                embed={config.public_welcome.embed}
                onChange={(embed) =>
                  setConfig((c) => ({
                    ...c,
                    public_welcome: { ...c.public_welcome, embed },
                  }))
                }
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <Megaphone className="w-8 h-8 text-gray-700 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Enable to configure public welcome messages</p>
            <p className="text-gray-600 text-xs mt-1">
              New members will see a welcome embed in the selected channel
            </p>
          </div>
        )}
      </Section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  DM WELCOME SECTION                                        */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <Section
        title="Private DM Welcome"
        subtitle="Send a private welcome embed directly to the new member's DMs"
        icon={Mail}
        enabled={dmEnabled}
        onToggle={() =>
          setConfig((c) => ({
            ...c,
            dm_welcome: { ...c.dm_welcome, enabled: !c.dm_welcome.enabled },
          }))
        }
        badge={dmEnabled ? 'Active' : undefined}
        defaultOpen={true}
      >
        {dmEnabled ? (
          <div className="space-y-5">
            {/* DM Notice */}
            <div className="bg-[#5865F2]/8 border border-[#5865F2]/15 rounded-xl px-4 py-3 flex items-start gap-3">
              <Shield className="w-4 h-4 text-[#8b9cf7] mt-0.5 shrink-0" />
              <div>
                <p className="text-[#8b9cf7] text-sm font-medium">Private Message</p>
                <p className="text-[#8b9cf7]/70 text-xs mt-0.5">
                  This embed is sent directly to the new member's DMs. Users with DMs disabled won't receive it.
                  Use this for private instructions, rules, or links you don't want in public channels.
                </p>
              </div>
            </div>

            {/* Embed Builder */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wider flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                DM Embed
              </p>
              <EmbedBuilder
                embed={config.dm_welcome.embed}
                onChange={(embed) =>
                  setConfig((c) => ({
                    ...c,
                    dm_welcome: { ...c.dm_welcome, embed },
                  }))
                }
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <Mail className="w-8 h-8 text-gray-700 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Enable to configure DM welcome messages</p>
            <p className="text-gray-600 text-xs mt-1">
              New members will receive a private embed in their DMs when they join
            </p>
          </div>
        )}
      </Section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/*  TIPS & INFO                                               */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="bg-dark-800/40 border border-white/[0.06] rounded-xl p-4 space-y-3">
        <h4 className="text-sm font-semibold text-white flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          Tips & Best Practices
        </h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-primary text-[10px] font-bold">1</span>
            </div>
            <p className="text-gray-400 text-xs leading-relaxed">
              <strong className="text-gray-300">Use both together</strong> — public channel for a short greeting,
              DMs for detailed instructions and rules.
            </p>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-primary text-[10px] font-bold">2</span>
            </div>
            <p className="text-gray-400 text-xs leading-relaxed">
              <strong className="text-gray-300">Add images</strong> — use the Image URL field in the embed builder
              to add a welcome banner or server logo.
            </p>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-primary text-[10px] font-bold">3</span>
            </div>
            <p className="text-gray-400 text-xs leading-relaxed">
              <strong className="text-gray-300">Personalize</strong> — use {'{user}'}, {'{server}'}, and {'{inviter}'} variables
              to make each welcome feel unique.
            </p>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-primary text-[10px] font-bold">4</span>
            </div>
            <p className="text-gray-400 text-xs leading-relaxed">
              <strong className="text-gray-300">DM privacy</strong> — some users have DMs disabled. The bot will
              silently skip them without errors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
